import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Field, controlBase, controlSize, type ControlSize } from './Field';

// `size` is omitted from the native attributes deliberately: on an <input> it means a character
// count, and we want it to mean height. Nothing in this codebase used the native one.
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  size?: ControlSize;
  /** Layout for the field as a whole — grid span, width. `className` styles the input itself. */
  fieldClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftAddon, rightAddon, size = 'sm', className, fieldClassName, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <Field label={label} htmlFor={inputId} error={error} hint={hint} className={fieldClassName}>
        <div className="relative flex items-center">
          {leftAddon && (
            <div className="absolute left-3 text-gray-400 dark:text-gray-500 pointer-events-none">
              {leftAddon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              controlBase(!!error),
              controlSize[size],
              leftAddon  ? 'pl-9' : 'pl-3',
              rightAddon ? 'pr-9' : 'pr-3',
              className,
            )}
            {...props}
          />
          {rightAddon && (
            <div className="absolute right-3 text-gray-400 dark:text-gray-500">{rightAddon}</div>
          )}
        </div>
      </Field>
    );
  },
);

Input.displayName = 'Input';
